import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowTurnDown,
  faArrowUp,
  faPaperclip,
  faScaleBalanced,
  faPen,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import Loading from "components/Loading";

import councilService, { councilQueryKeys } from "api/councilService";
import {
  ECouncilAgendaItemType,
  ECouncilSessionState,
  ICouncilAgendaAttachment,
  ICouncilLegalActOption,
  ICouncilAgendaItem,
  ICouncilGuest,
  ICouncilSession,
} from "dto/Council/ICouncil";
import { ENotificationType, notification } from "utils/notification";

import AttachLegalActModal from "./AttachLegalActModal";
import AttachmentTitleModal from "./AttachmentTitleModal";
import AttachmentsListModal from "./AttachmentsListModal";
import GuestModal from "./GuestModal";
import {
  buildDefaultAgenda,
  createChildItem,
  createRootItem,
  levelOf,
  moveItem,
  removeItemRecursive,
  renumerate,
} from "./agendaOrder";
import { decodeAgendaItems, decodeGuest } from "./decodeText";
import { newId } from "./newId";
import { uploadPublicFile } from "./uploadPublicFile";
import styles from "./styles.module.scss";

interface Props {
  session: ICouncilSession;
  /** Archiwum otwiera porządek w trybie podglądu, bez przycisku „Nowy punkt". */
  forceReadOnly?: boolean;
  onSessionChanged?: (session: ICouncilSession) => void;
}

// Edytor porządku obrad — odwzorowanie AgendaView + AgendaViewModel z Magistratu.
//
// Lewa kolumna: drzewo punktów (numeracja „1", „1.2", „1.2.3") z paskiem narzędzi na
// hover: Edytuj / Dodaj podrzędny / Dodaj załącznik / Przesuń w górę / Przesuń w dół / Usuń.
// Prawa kolumna: lista gości sesji.
//
// Jak w oryginale zapis jest automatyczny (AutoCommitChanges): każda zmiana struktury
// albo zatwierdzenie tekstu punktu od razu leci do BE, a odpowiedź (z mapowaniem
// tymczasowych Id na nadane przez serwer) staje się nowym stanem listy.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const AgendaEditor = ({ session, forceReadOnly = false, onSessionChanged }: Props) => {
  const [items, setItems] = useState<ICouncilAgendaItem[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [attachmentsFor, setAttachmentsFor] = useState<string | null>(null);
  const [actFor, setActFor] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<{ itemId: string; file: File } | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [toRemove, setToRemove] = useState<ICouncilAgendaItem | null>(null);
  const [guestModal, setGuestModal] = useState<{ guest: ICouncilGuest | null } | null>(
    null,
  );
  const [guestToRemove, setGuestToRemove] = useState<ICouncilGuest | null>(null);
  /** „Edytuj" na sesji zakończonej — odblokowuje porządek mimo stanu Finished. */
  const [forceEdit, setForceEdit] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileTargetRef = useRef<string | null>(null);
  // Upload jest asynchroniczny — po jego zakończeniu domknięcie `items` może być już
  // nieaktualne, więc do doklejenia załącznika bierzemy stan z refa.
  const itemsRef = useRef<ICouncilAgendaItem[]>([]);
  const suppressBlurUntil = useRef(0);
  // Trwająca edycja trzymana także w refie. Kliknięcie w pasek narzędzi wywołuje najpierw
  // blur, a to OSOBNE zdarzenie przeglądarki — React zdąży przerenderować komponent i do
  // czasu obsługi kliknięcia `editingId` jest już null. Ref przenosi niezatwierdzony tekst
  // przez tę granicę, żeby akcja mogła go dopisać do zapisu.
  const pendingEditRef = useRef<{ id: string; title: string } | null>(null);
  // Zapisy porządku są pełnymi snapshotami, więc nie mogą lecieć równolegle: odpowiedź
  // starszego żądania cofnęłaby listę, a kolejna akcja utrwaliłaby ten cofnięty stan.
  // Trzymamy więc jedno żądanie w locie, a kolejne zmiany czekają w `queuedSaveRef`.
  const savingRef = useRef(false);
  const queuedSaveRef = useRef<ICouncilAgendaItem[] | null>(null);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const queryClient = useQueryClient();

  const agendaQuery = councilService.useGetAgenda(session.Id);
  const guestsQuery = councilService.useGetGuests(session.Id);
  const { mutate: saveAgenda, isLoading: saving } = councilService.useSaveAgenda();
  const { mutate: saveGuest, isLoading: savingGuest } = councilService.useSaveGuest();
  const { mutate: removeGuest } = councilService.useRemoveGuest();

  const guests = (guestsQuery.data?.data ?? []).map(decodeGuest);

  const readOnly =
    forceReadOnly ||
    session.State === ECouncilSessionState.Closed ||
    (session.State === ECouncilSessionState.Finished && !forceEdit);

  // Wczytanie porządku. Pusta sesja dostaje domyślną strukturę (pusty punkt 1 +
  // statyczny punkt „Przedstawienie porządku obrad") — tak jak w Magistracie.
  useEffect(() => {
    if (!agendaQuery.data) return;

    const loaded = agendaQuery.data.data ?? [];

    if (loaded.length) {
      setItems(renumerate(decodeAgendaItems(loaded)));
      return;
    }

    if (forceReadOnly || session.State !== ECouncilSessionState.NotStarted) {
      setItems([]);
      return;
    }

    const initial = buildDefaultAgenda(newId);
    setItems(initial);
    setEditing(initial[0].Id, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaQuery.data, session.Id]);

  useEffect(() => {
    setForceEdit(false);
  }, [session.Id]);

  const persist = (next: ICouncilAgendaItem[]) => {
    setItems(next);

    // Jedno żądanie w locie; ostatni stan czeka w kolejce i poleci po odpowiedzi.
    if (savingRef.current) {
      queuedSaveRef.current = next;
      return;
    }

    savingRef.current = true;

    saveAgenda(
      { sessionId: session.Id, request: { Items: next } },
      {
        onSuccess: (res) => {
          const map = res.data.NewItems ?? {};
          const remap = (id: string) => map[id] ?? id;

          // Tymczasowe Id zamieniamy na nadane przez serwer wszędzie, gdzie je trzymamy —
          // inaczej trwająca edycja, otwarty modal załączników albo kończący się upload
          // wskazywałyby na punkt, którego już nie ma na liście.
          if (pendingEditRef.current)
            pendingEditRef.current.id = remap(pendingEditRef.current.id);

          if (fileTargetRef.current) fileTargetRef.current = remap(fileTargetRef.current);

          setPendingFile((prev) =>
            prev ? { ...prev, itemId: remap(prev.itemId) } : prev,
          );
          setEditingId((prev) => (prev ? remap(prev) : prev));
          setAttachmentsFor((prev) => (prev ? remap(prev) : prev));

          const server = decodeAgendaItems(res.data.Items ?? []);

          setItems((prev) => {
            // Punkty dodane lokalnie już PO wysłaniu żądania nie mogły trafić do zapisu;
            // odpowiedź serwera ich nie zawiera, więc doklejamy je z powrotem, zamiast
            // kasować użytkownikowi wiersz w trakcie pisania.
            const onServer = new Set(server.map((x) => x.Id));
            const sent = new Set(next.map((x) => x.Id));

            const local = prev
              .filter((x) => !onServer.has(x.Id) && !sent.has(x.Id))
              .map((x) => ({
                ...x,
                ParentId: x.ParentId ? remap(x.ParentId) : x.ParentId,
              }));

            return renumerate(local.length ? [...server, ...local] : server);
          });

          // Cache react-query musi znać nowy stan — inaczej po powrocie do sesji edytor
          // wystartuje ze starym snapshotem i odeśle go jako pełny zapis.
          queryClient.setQueryData([councilQueryKeys.agenda, session.Id], {
            data: res.data.Items ?? [],
          });

          // Zapis porządku kasuje wcześniejszy podpis elektroniczny.
          if (session.IsSigned) onSessionChanged?.({ ...session, IsSigned: false });
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Nie zapisano porządku obrad",
            text: errorText(e, "Nie udało się zapisać porządku obrad."),
          }),
        onSettled: () => {
          savingRef.current = false;

          const queued = queuedSaveRef.current;
          queuedSaveRef.current = null;

          if (queued) persist(queued);
        },
      },
    );
  };

  /**
   * Edycja podpisanego porządku kasuje podpis — jak w Magistracie pytamy o zgodę.
   * (Podpisywanie porządku to etap 2, więc dziś warunek jest zwykle nieaktywny.)
   */
  const verifySigned = (action: () => void) => {
    if (!session.IsSigned) {
      action();
      return;
    }

    if (
      window.confirm(
        "Porządek obrad został podpisany.\n\nEdycja porządku obrad spowoduje usunięcie wcześniej złożonego podpisu elektronicznego. Czy chcesz kontynuować?",
      )
    ) {
      action();
    }
  };

  /** Otwiera/zamyka edycję punktu, trzymając stan także w refie. */
  const setEditing = (id: string | null, title = "") => {
    setEditingId(id);
    setDraft(title);
    pendingEditRef.current = id ? { id, title } : null;
  };

  const onDraftChange = (value: string) => {
    setDraft(value);

    if (pendingEditRef.current) pendingEditRef.current.title = value;
  };

  /** Lista z doklejonym tekstem punktu, który jest właśnie edytowany (jeszcze niezapisanym). */
  const withPendingEdit = (source: ICouncilAgendaItem[]) => {
    const pending = pendingEditRef.current;

    return pending
      ? source.map((x) => (x.Id === pending.id ? { ...x, Title: pending.title } : x))
      : source;
  };

  /** Czy tekst edytowanego punktu faktycznie się zmienił. */
  const hasPendingChange = (source: ICouncilAgendaItem[]) => {
    const pending = pendingEditRef.current;

    if (!pending) return false;

    const current = source.find((x) => x.Id === pending.id);

    return !!current && (current.Title ?? "") !== pending.title;
  };

  /**
   * Zamyka trwającą edycję i zwraca listę z doklejonym tekstem. Sama nie zapisuje —
   * akcja, która to wywołała, i tak wykona własny (jedyny) zapis.
   */
  const endEdit = () => {
    const base = withPendingEdit(items);

    setEditing(null);
    suppressBlurUntil.current = 0;

    return base;
  };

  /** Zamyka edycję i zapisuje ją, jeżeli akcja wywołująca sama nic nie zapisze. */
  const flushPending = () => {
    const changed = hasPendingChange(items);
    const base = endEdit();

    if (changed) persist(base);
  };

  /**
   * Kliknięcie w pasek narzędzi najpierw wywołuje blur edytowanego punktu. Bez tej
   * blokady poleciałyby dwa zapisy: jeden z blur, drugi z akcji — a ten drugi mógłby
   * nadpisać świeży tekst starym. Blokada jest krótkotrwała, żeby nie zawisła
   * na stałe, gdyby po mousedown nie doszło do kliknięcia.
   */
  const armSuppress = () => {
    suppressBlurUntil.current = Date.now() + 500;
  };

  const commitEdit = () => {
    const suppressed = suppressBlurUntil.current > Date.now();
    suppressBlurUntil.current = 0;

    if (!pendingEditRef.current) return;

    // Zapis przejmie akcja, która właśnie została kliknięta — zamykamy tylko pole
    // edycji, a niezatwierdzony tekst zostaje w refie do jej dyspozycji.
    if (suppressed) {
      setEditingId(null);
      return;
    }

    flushPending();
  };

  const startEdit = (item: ICouncilAgendaItem) => {
    // Przejście do innego punktu musi najpierw utrwalić tekst poprzedniego.
    flushPending();

    setEditing(item.Id, item.Title ?? "");
  };

  // Nowy punkt zostaje na razie tylko lokalnie — jak w Magistracie zapisuje się dopiero
  // razem z pierwszą zatwierdzoną treścią (albo przy najbliższej innej zmianie porządku).
  // Dzięki temu wiersz nie przeładowuje się w trakcie pisania po nadaniu mu Id przez serwer.
  const addRootItem = () =>
    verifySigned(() => {
      const base = endEdit();
      const item = createRootItem(base, newId());

      setItems([...base, item]);
      setEditing(item.Id, "");
    });

  const addChildItem = (parent: ICouncilAgendaItem) =>
    verifySigned(() => {
      const base = endEdit();
      const item = createChildItem(base, parent, newId());

      setItems([...base, item]);
      setEditing(item.Id, "");
    });

  const move = (item: ICouncilAgendaItem, direction: "up" | "down") =>
    verifySigned(() => persist(moveItem(endEdit(), item.Id, direction)));

  const requestRemove = (item: ICouncilAgendaItem) => {
    if (item.HasVotings) {
      notification({
        type: ENotificationType.INFO,
        title: "Nie można usunąć",
        text: "W wybranym punkcie przeprowadzono głosowania. Nie można go usunąć.",
      });
      return;
    }

    if (item.HasActions) {
      notification({
        type: ENotificationType.INFO,
        title: "Nie można usunąć",
        text: "Z wybranym punktem powiązane są już głosowania lub zdarzenia protokołu. Nie można go usunąć.",
      });
      return;
    }

    verifySigned(() => {
      flushPending();
      setToRemove(item);
    });
  };

  const pickFile = (item: ICouncilAgendaItem) =>
    verifySigned(() => {
      flushPending();
      fileTargetRef.current = item.Id;
      fileInputRef.current?.click();
    });

  const onFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const itemId = fileTargetRef.current;

    // Reset, żeby wybór tego samego pliku drugi raz też wywołał zdarzenie.
    event.target.value = "";

    if (file && itemId) setPendingFile({ itemId, file });
  };

  const attachFile = async (title: string) => {
    if (!pendingFile) return;

    setUploading(true);

    try {
      const uploaded = await uploadPublicFile(pendingFile.file);

      const attachment: ICouncilAgendaAttachment = {
        Id: newId(),
        AgendaItemId: pendingFile.itemId,
        Title: title,
        FileName: uploaded.FileName,
        ContentType: uploaded.ContentType,
        Size: uploaded.Size,
        FileUri: uploaded.Uri,
        SortOrder: 0,
      };

      persist(
        itemsRef.current.map((x) =>
          x.Id === pendingFile.itemId
            ? { ...x, Attachments: [...(x.Attachments ?? []), attachment] }
            : x,
        ),
      );

      setPendingFile(null);
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Nie dodano załącznika",
        text: (e as Error)?.message ?? "Nie udało się wysłać pliku.",
      });
    } finally {
      setUploading(false);
    }
  };

  /**
   * Dołączenie projektu aktu jako RELACJI — punkt zyskuje odnośnik do aktu, a nie kopię
   * pliku. Serwer sprawdza, czy akt należy do tego profilu, i sam uzupełnia jego metrykę.
   */
  const attachActs = (itemId: string, acts: ICouncilLegalActOption[]) =>
    persist(
      itemsRef.current.map((x) =>
        x.Id === itemId
          ? {
              ...x,
              Attachments: [
                ...(x.Attachments ?? []),
                ...acts.map((act) => ({
                  Id: newId(),
                  AgendaItemId: itemId,
                  Title: act.Title,
                  FileName: null,
                  ContentType: null,
                  Size: 0,
                  FileUri: null,
                  SortOrder: 0,
                  LegalActId: act.Id,
                  ActNumber: act.ActNumber,
                  ActTypeName: act.ActTypeName,
                })),
              ],
            }
          : x,
      ),
    );

  const changeAttachments = (
    itemId: string,
    attachments: ICouncilAgendaAttachment[],
  ) =>
    persist(
      items.map((x) => (x.Id === itemId ? { ...x, Attachments: attachments } : x)),
    );

  const applyGuest = (request: Parameters<typeof saveGuest>[0]["request"]) =>
    saveGuest(
      { sessionId: session.Id, request },
      {
        onSuccess: () => {
          setGuestModal(null);
          guestsQuery.refetch();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się zapisać gościa."),
          }),
      },
    );

  const applyRemoveGuest = () => {
    if (!guestToRemove) return;

    removeGuest(guestToRemove.Id, {
      onSuccess: () => guestsQuery.refetch(),
      onError: (e) =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: errorText(e, "Nie udało się usunąć gościa."),
        }),
    });

    setGuestToRemove(null);
  };

  // Akty już dołączone w tej sesji — modal wyszarza je, żeby nie dublować relacji.
  const attachedActIds = useMemo(
    () =>
      items
        .flatMap((x) => x.Attachments ?? [])
        .map((a) => a.LegalActId)
        .filter((id): id is string => !!id),
    [items],
  );

  const attachmentsItem = useMemo(
    () => items.find((x) => x.Id === attachmentsFor) ?? null,
    [items, attachmentsFor],
  );

  if (agendaQuery.isLoading) {
    return <Loading />;
  }

  return (
    <div className={styles.agendaWrap}>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={onFilePicked}
      />

      <div className={styles.agendaColumn}>
        {session.State === ECouncilSessionState.Finished && !forceReadOnly && (
          <div className={styles.readOnlyNote}>
            Sesja jest zakończona — porządek obrad jest tylko do odczytu.
            {!forceEdit && (
              <>
                {" "}
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={() => setForceEdit(true)}
                >
                  Edytuj
                </button>
              </>
            )}
          </div>
        )}

        <div className={styles.agendaList}>
          {!items.length && (
            <div className={styles.emptyAgenda}>Dodaj pierwszy element</div>
          )}

          {items.map((item) => {
            const isStatic = item.Type === ECouncilAgendaItemType.StaticApproveAgenda;
            const isEditing = editingId === item.Id;
            const attachmentsCount = item.Attachments?.length ?? 0;

            return (
              <div
                key={item.Id}
                className={styles.itemRow}
                style={{ marginLeft: levelOf(item) * 24 }}
              >
                <div className={styles.itemOrder}>{item.Order}</div>

                <div className={styles.itemContent}>
                  {isEditing ? (
                    <textarea
                      className={styles.itemInput}
                      value={draft}
                      autoFocus
                      maxLength={2000}
                      onChange={(e) => onDraftChange(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          commitEdit();
                        }
                        if (e.key === "Escape") setEditing(null);
                      }}
                    />
                  ) : (
                    <div
                      className={`${styles.itemText} ${
                        !item.Title ? styles.itemTextEmpty : ""
                      } ${isStatic ? styles.itemStatic : ""}`}
                      onDoubleClick={() => !readOnly && verifySigned(() => startEdit(item))}
                    >
                      {item.Title || "Kliknij dwukrotnie, aby wpisać treść punktu"}
                    </div>
                  )}

                  {attachmentsCount > 0 && (
                    <button
                      type="button"
                      className={styles.attachmentsBadge}
                      onMouseDown={armSuppress}
                      title={(item.Attachments ?? [])
                        .map((a) => a.Title || a.FileName)
                        .join("\n")}
                      onClick={() => {
                        flushPending();
                        setAttachmentsFor(item.Id);
                      }}
                    >
                      <FontAwesomeIcon icon={faPaperclip} />
                      {attachmentsCount}
                    </button>
                  )}
                </div>

                {!readOnly && (
                  <div className={styles.itemTools}>
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Edytuj"
                      onMouseDown={armSuppress}
                      onClick={() => verifySigned(() => startEdit(item))}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Dodaj podrzędny"
                      disabled={isStatic}
                      onMouseDown={armSuppress}
                      onClick={() => addChildItem(item)}
                    >
                      <FontAwesomeIcon icon={faArrowTurnDown} />
                    </button>
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Dodaj załącznik"
                      onMouseDown={armSuppress}
                      onClick={() => pickFile(item)}
                    >
                      <FontAwesomeIcon icon={faPaperclip} />
                    </button>
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Dołącz projekt uchwały"
                      onMouseDown={armSuppress}
                      onClick={() => verifySigned(() => setActFor(item.Id))}
                    >
                      <FontAwesomeIcon icon={faScaleBalanced} />
                    </button>
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Przesuń w górę"
                      onMouseDown={armSuppress}
                      onClick={() => move(item, "up")}
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Przesuń w dół"
                      onMouseDown={armSuppress}
                      onClick={() => move(item, "down")}
                    >
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                    <button
                      type="button"
                      className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                      title="Usuń"
                      onMouseDown={armSuppress}
                      onClick={() => requestRemove(item)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.agendaFooter}>
          {!readOnly && (
            <span onMouseDown={armSuppress}>
              <Button text="Nowy punkt" onClick={addRootItem} />
            </span>
          )}
          {saving && <span className={styles.fieldLabel}>Zapisywanie…</span>}
        </div>
      </div>

      <div className={styles.guestsColumn}>
        <div className={styles.guestsHeader}>
          <span className={styles.guestsTitle}>Goście</span>
          {!readOnly && (
            <button
              type="button"
              className={styles.toolBtn}
              title="Dodaj gościa"
              onClick={() => setGuestModal({ guest: null })}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          )}
        </div>

        <div className={styles.guestsList}>
          {!guests.length && (
            <div className={styles.emptyGuests}>Nie dodano jeszcze gości.</div>
          )}

          {guests.map((guest) => (
            <div key={guest.Id} className={styles.guestRow}>
              <div>
                <div className={styles.guestName}>
                  {[guest.LastName, guest.FirstName].filter(Boolean).join(" ")}
                </div>
                <div className={styles.guestRole}>
                  {guest.Role || (guest.Male ? "Gość" : "Gość")}
                </div>
              </div>

              {!readOnly && (
                <div className={styles.guestTools}>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Edytuj"
                    onClick={() => setGuestModal({ guest })}
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                    title="Usuń"
                    onClick={() => setGuestToRemove(guest)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {actFor && (
        <AttachLegalActModal
          sessionId={session.Id}
          attachedIds={attachedActIds}
          onClose={() => setActFor(null)}
          onApply={(acts) => {
            attachActs(actFor, acts);
            setActFor(null);
          }}
        />
      )}

      {attachmentsItem && (
        <AttachmentsListModal
          item={attachmentsItem}
          readOnly={readOnly}
          onClose={() => setAttachmentsFor(null)}
          onChange={(attachments) =>
            changeAttachments(attachmentsItem.Id, attachments)
          }
        />
      )}

      {pendingFile && (
        <AttachmentTitleModal
          initialTitle={pendingFile.file.name.replace(/\.[^.]+$/, "")}
          fileName={pendingFile.file.name}
          loading={uploading}
          onClose={() => setPendingFile(null)}
          onApply={attachFile}
        />
      )}

      {toRemove && (
        <ConfirmModal
          title="Usunąć?"
          text="Usunąć wybrany element? Zostaną usunięte także jego punkty podrzędne i załączniki."
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={() => {
            persist(removeItemRecursive(items, toRemove.Id));
            setToRemove(null);
          }}
          onCancel={() => setToRemove(null)}
        />
      )}

      {guestModal && (
        <GuestModal
          guest={guestModal.guest}
          loading={savingGuest}
          onClose={() => setGuestModal(null)}
          onApply={applyGuest}
        />
      )}

      {guestToRemove && (
        <ConfirmModal
          title="Usunąć?"
          text="Czy na pewno chcesz usunąć wybranego gościa z listy?"
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={applyRemoveGuest}
          onCancel={() => setGuestToRemove(null)}
        />
      )}
    </div>
  );
};

export default AgendaEditor;
